import { Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { translate } from '@jsverse/transloco';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatOptgroup, MatOption, MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { KeyValue } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { isEmpty, isEqual } from 'lodash-es';
import { MatTooltip } from '@angular/material/tooltip';
import {
    BaseFieldComponent,
    ButtonComponent,
    InputComponent,
    TextRendererPipe,
    TooltipTextComponent,
} from 'vcp-common-ui-lib';

@Component({
    selector: 'vc-multi-select-with-groups',
    imports: [
        MatLabel,
        MatFormField,
        MatFormFieldModule,
        MatSelect,
        FormsModule,
        InputComponent,
        MatOption,
        TooltipTextComponent,
        MatIcon,
        ButtonComponent,
        MatTooltip,
        TextRendererPipe,
        InputComponent,
        MatOptgroup,
        MatOptgroup,
    ],
    templateUrl: './multi-select-with-groups.component.html',
    styleUrl: './multi-select-with-groups.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class MultiSelectWithGroupsComponent<T> extends BaseFieldComponent<T> {
    filter: string = '';
    tooltipDisabled: boolean = true;
    filteredOptions!: KeyValue<string, T[]>[];

    /** Whether to show filter */
    @Input()
    showFilter: boolean = true;

    /** Whether to collapse dropdown on value change*/
    @Input()
    closePanelOnValueChange: boolean = false;

    /** Multi select filter label */
    @Input()
    filterLabel: string = translate('COMMON_UI.SEARCH');

    /** Multi select filter placeholder */
    @Input()
    filterPlaceholder: string = '';

    /** Suffix icon. */
    @Input()
    suffixIcon!: string;

    @Input()
    set values(val: KeyValue<string, T>[]) {
        if (!isEqual(val, this._values)) {
            this._values = val;
            if (val && !isEmpty(val)) {
                this._groupedValues = this._getGroup(val);
            } else {
                this._groupedValues = [];
            }
            this.valuesChange.emit(this._values);
            if (this.closePanelOnValueChange) {
                this.selectInput?.close();
            }
        }
    }
    get values(): KeyValue<string, T>[] {
        return this._values;
    }
    private _values: KeyValue<string, T>[] = [];

    set groupedValues(val: KeyValue<string, T>[]) {
        this._groupedValues = val;
        this._values = val ?? [];
        this.valuesChange.emit(this._values);
        if (this.closePanelOnValueChange) {
            this.selectInput?.close();
        }
    }
    get groupedValues(): KeyValue<string, T>[] {
        return this._groupedValues;
    }
    private _groupedValues: KeyValue<string, T>[] = [];

    /** Dropdown options */
    @Input()
    set options(val: KeyValue<string, T[]>[]) {
        if (val) {
            this._options = val;
            this.filteredOptions = this._options;
        }
    }
    get options(): KeyValue<string, T[]>[] {
        return this._options;
    }
    private _options: KeyValue<string, T[]>[] = [];

    /** Whether to show clear icon. */
    @Input()
    showClear: boolean = false;

    /** Whether to show clear icon for filter field. */
    @Input()
    showClearForFilter: boolean = true;

    /** Whether to show a tooltip upon hover based on itemRenderer and comma separated, recommended for use with minimal styles in table filters */
    @Input()
    showTooltip: boolean = true;

    @Input()
    tooltipPosition: 'above' | 'below' | 'before' | 'after' = 'below';

    @Input()
    compareWith: (a: KeyValue<string, T>, b: KeyValue<string, T>) => boolean = (a, b) => isEqual(a, b);

    /** Function for rendering group names */
    @Input()
    groupRenderer: (data: string) => string = (value: string): string => `${value}`;

    /** Function for rendering options and selected values */
    @Input()
    optionRenderer: (data: T) => string = (value: T): string => `${value}`;

    /** Function for rendering options inside the hover tooltip */
    @Input()
    tooltipOptionRenderer!: (data: T) => string;

    /** Two ways data binding for multi select values */
    @Output()
    valuesChange: EventEmitter<KeyValue<string, T>[]> = new EventEmitter<KeyValue<string, T>[]>();

    /** Event is fired when dropdown visible changes */
    @Output()
    openChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('selectInput')
    selectInput!: MatSelect;

    @ViewChild('inputElement')
    inputElement!: InputComponent;

    private _getGroup(values: KeyValue<string, T>[]): KeyValue<string, T>[] {
        return values ?? [];
    }

    override clear(): void {
        if (!this.readonly && !this.disabled) {
            this.values = [];
        }
        this.valid = true;
        this.errorMessage = '';
    }

    openedChange(value: boolean): void {
        this.openChange.emit(value);
        this.filter = '';
        if (value) {
            this.focus();
        }
    }

    focus(): void {
        this.inputElement?.inputElement?.nativeElement?.focus();
    }

    getTooltipText: (value: KeyValue<string, T>[]) => string = (value: KeyValue<string, T>[]) => {
        if (this.showTooltip && value) {
            const tooltip: string[] = [];
            value.forEach((pair: KeyValue<string, T>) =>
                tooltip.push(
                    this.tooltipOptionRenderer
                        ? this.tooltipOptionRenderer(pair.value)
                        : this.optionRenderer(pair.value)
                )
            );
            return tooltip.join(', ');
        }

        return '';
    };

    checkIfShowTooltip(el: MatSelect) {
        const parent = el._elementRef.nativeElement.querySelector('.mat-mdc-select-value');
        const child = el._elementRef.nativeElement.querySelector('.mat-mdc-select-value-text');
        this.tooltipDisabled = parent && child ? parent.offsetWidth > child.offsetWidth : true;
    }

    checkIfShowGroup(group: KeyValue<string, T[]>): boolean {
        return group.value.some(this.checkIfShowOption);
    }

    checkIfShowOption = (item: T) => {
        const regExp = new RegExp('\\b' + this.filter, 'gi');
        return regExp.test(this.optionRenderer(item));
    };
}

export interface TemplateContext<T> {
    $implicit: T;
    index?: number;
}
